import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import querystring from "query-string";
import { api } from "@/src/core/api/client";
import { reviewService } from "../services";
import type {
  ICreateReviewRequest,
  IPublicReviewPage,
  IReviewPage,
  IReviewPageQuery,
} from "../types";

const PAGE_SIZE = 10;

const reviewKeys = {
  all: ["reviews"] as const,
  listing: (listingId: string | number) =>
    [...reviewKeys.all, "listing", String(listingId)] as const,
  publicListing: (listingId: number, mention: string | null) =>
    [...reviewKeys.all, "public", listingId, mention ?? "all"] as const,
};

/** Raw calls (Laravel). Throw an ApiError on failure. */
const reviewsApi = {
  async getListingReviews(
    listingId: string | number,
    query: IReviewPageQuery,
  ): Promise<IReviewPage> {
    const { data } = await api.laravel.get<IReviewPage>(
      `/review?listing=${listingId}&${querystring.stringify(query)}`,
    );
    return data;
  },

  /**
   * The app's reviews API (Python): one page, plus the summary (average, mention chips,
   * badges). `mention` filters to one chip's reviews.
   */
  async getPublicListingReviews(
    listingId: number,
    pageNumber: number,
    mention: string | null,
  ): Promise<IPublicReviewPage> {
    const { data } = await api.python.get<IPublicReviewPage>(
      `/public/listings/${listingId}/reviews`,
      {
        params: {
          page: pageNumber,
          per_page: PAGE_SIZE,
          ...(mention ? { filters: JSON.stringify([mention]) } : {}),
        },
      },
    );
    return data;
  },

  async createReview(review: ICreateReviewRequest): Promise<void> {
    await api.laravel.post("/review/listing", review);
  },
};

/** A listing's reviews, 10 per page, loaded page by page. */
export const useListingReviewsQuery = (
  listingId: string | number | undefined,
) =>
  useInfiniteQuery({
    queryKey: reviewKeys.listing(listingId ?? "none"),
    queryFn: ({ pageParam }) =>
      reviewsApi.getListingReviews(listingId as string | number, {
        limit: PAGE_SIZE,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: reviewService.nextPage,
    enabled: listingId !== undefined && listingId !== "",
  });

/** A listing's reviews from the app's API, 10 per page; `mention` narrows to one chip. */
export const usePublicListingReviewsQuery = (
  listingId: number,
  mention: string | null = null,
) =>
  useInfiniteQuery({
    queryKey: reviewKeys.publicListing(listingId, mention),
    queryFn: ({ pageParam }) =>
      reviewsApi.getPublicListingReviews(listingId, pageParam, mention),
    initialPageParam: 1,
    getNextPageParam: reviewService.nextPublicPage,
  });

/** Posts a guest's review of a finished booking. */
export const useCreateReviewMutation = () =>
  useMutation({ mutationFn: reviewsApi.createReview });
